import { useState } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import Notice from "@/types/Notice";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { ArrowUpDown, Plus } from "lucide-react";
import Link from "@/components/atoms/Link";
import Badge from "@/components/atoms/Badge";
import Container from "@/components/atoms/Container";
import useAccess from "@/hooks/useAccess";
import Pagination from "@/components/atoms/Pagination";
import { router } from "@inertiajs/react";

type Props = {
  notices: Notice[];
};

const columns: ColumnDef<Notice>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
  },
  {
    accessorKey: "notice_message",
    header: ({ column }) => <ColumnHeader column={column}>Message</ColumnHeader>,
  },
  {
    accessorKey: "start_date",
    header: ({ column }) => <ColumnHeader column={column}>Start Date</ColumnHeader>,
  },
  {
    accessorKey: "end_date",
    header: ({ column }) => <ColumnHeader column={column}>End Date</ColumnHeader>,
  },
  {
    accessorKey: "notice_active",
    header: ({ column }) => <ColumnHeader column={column}>Status</ColumnHeader>,
    cell: ({ row }) => (
      <div className="space-x-2">
        <Badge
          variant={
            row.original.notice_active
              ? "success"
              : "destructive"
          }
          className="p-1"
        />
        <span>{row.original.notice_active ? 'Active' : 'Inactive'}</span>
      </div>
    ),
  },
  {
    accessorKey: "hide_from_client",
    header: ({ column }) => <ColumnHeader column={column}>Client Visibility</ColumnHeader>,
    cell: ({ row }) => (
      <div className="space-x-2">
        <Badge
          variant={
            row.original.hide_from_client
              ? "destructive"
              : "success"
          }
          className="p-1"
        />
        <span>{row.original.hide_from_client ? 'Invisible' : 'Visible'}</span>
      </div>
    ),
  },
];

const Index = ({ notices }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalCount, setTotalCount] = useState<number>(notices.length);
  const [totalPages, setTotalPages] = useState<number>(Math.ceil(totalCount / pagination.per_page));
  const [filteredAccounts, setFilteredAccounts] = useState<Notice[]>(notices);
  

  const { getAccess } = useAccess();
  const breadcrumbItems = [{ label: "Notices", onClick: () => router.get("/notices"), }];

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };



  return (
    <>
      <Container>
        <PageHeader title="Notices" breadcrumbItems={breadcrumbItems}>
          <div className="space-x-2">
          {getAccess(["create_notice"]) ? (
            <Link href="/notices/new">
              <Button className="inline-flex items-center gap-2 text-black">
                <Plus size={16} /> <span>Create Notice</span>
              </Button>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
              <Button className="inline-flex items-center gap-2 text-gray-500" disabled>
                <Plus size={16} /> <span>Create Notice</span>
              </Button>
            </span>
          )}
          </div>
        </PageHeader>
      </Container>
      <div className="border-b border-border" />
      <DataTable
        columns={columns}
        data={filteredAccounts}
        rowUrl={(row) => `${location.pathname}/${row.id}`}
        currentPage={pagination.page}
        pageSize={5}
      />
      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={5}
        onPageChange={handlePageChange}
      />

    </>
  );
};

export default Index;
